'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth, UserRole } from '@/context/AuthContext';
import Icon from '@/components/ui/AppIcon';

interface RouteGuardProps {
  requiredRole: UserRole;
  children: React.ReactNode;
}

export default function RouteGuard({ requiredRole, children }: RouteGuardProps) {
  const { user, isLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      router.replace('/login-with-2fa');
      return;
    }
    if (user.role !== requiredRole) {
      // Redirect to the correct home for their role
      router.replace(user.role === 'admin' ? '/admin-control-center' : '/broker-dashboard');
    }
  }, [user, isLoading, requiredRole, router]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-text-secondary">
          <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user || user.role !== requiredRole) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-text-secondary">
          <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
          <span className="text-sm">Redirecting...</span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
